import { Button, ButtonProps, CircularProgress, styled, Typography } from '@mui/material';
import { forwardRef } from 'react';
import { DynamicIcon } from 'src/components';
import { IconLibrary } from 'src/components/DynamicIcon/dynamic-icon';
import { toSxArray } from 'src/utils/sx';
import {
  secondaryButtonLabelSx,
  secondaryButtonRootSx,
  sendButtonLabelSx,
  sendButtonRootSx,
  SendSecondaryButtonSize,
} from './buttons.styles';

const StyledButton = styled(Button, {
  name: 'InputFieldSendButton',
})(({ theme }) => ({
  flexShrink: 0,
  padding: theme.spacing(0, 1.5),
  minHeight: 36,
  whiteSpace: 'nowrap',
  overflow: 'hidden',

  '@container (max-width: 30rem)': {
    '&': {
      padding: theme.spacing(0, 0.75),
    },

    '.button-text': {
      display: 'none',
    },
  },
  '.MuiButtonGroup': {
    boxShadow: 'none',
  },
  '.MuiButton-startIcon': {
    margin: 0,
  },
  '.MuiButton-endIcon': {
    margin: 0,
  },

  '&.MuiButton-contained': {
    boxShadow: theme.shadows[2],
    borderColor: theme.palette.background.paper,
  },

  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(0, 0.75),
  },
}));

export const SendButton = forwardRef<
  HTMLButtonElement,
  ButtonProps & {
    startIcon?: string;
    type?: string;
    isLoading?: boolean;
    buttonText?: string;
    /** Compact sizing for space-constrained toolbars. */
    size?: SendSecondaryButtonSize;
  }
>(({ buttonText, isLoading, startIcon = 'send', size = 'medium', sx, ...props }, ref) => {
  const isCompact = size === 'small';

  const icon = isCompact ? (
    // display:block + lineHeight:0 centers the icon's own box, not its text baseline
    <DynamicIcon
      library="lucide"
      name={startIcon}
      size={14}
      sx={{ display: 'block', lineHeight: 0 }}
    />
  ) : (
    <DynamicIcon
      sx={{
        height: 'auto',
        lineHeight: '1.4rem',
        fontSize: '1.4rem !important',
        margin: {
          xs: 0,
        },
      }}
      name={startIcon}
      weight={300}
    />
  );

  return (
    <StyledButton
      ref={ref}
      {...props}
      variant="contained"
      sx={[sendButtonRootSx(size), ...toSxArray(sx)]}
      startIcon={isLoading ? <CircularProgress size="1.25rem" color="primary" /> : icon}
    >
      <Typography className="button-text" variant="overline" sx={sendButtonLabelSx(size)}>
        {buttonText}
      </Typography>
    </StyledButton>
  );
});
SendButton.displayName = 'SendButton';

export const SecondaryButton = forwardRef<
  HTMLButtonElement,
  ButtonProps & {
    type?: string;
    isLoading?: boolean;
    buttonText?: string;
    icon?: string;
    iconLibrary?: IconLibrary;
    /** Compact sizing for space-constrained toolbars. */
    size?: SendSecondaryButtonSize;
  }
>(({ buttonText, icon, iconLibrary, isLoading, size = 'medium', sx, ...props }, ref) => {
  const isCompact = size === 'small';
  const buttonIcon = isLoading ? (
    <CircularProgress size="1.25rem" color="primary" />
  ) : (
    icon &&
    (isCompact ? (
      // display:block + lineHeight:0 centers the icon's own box, not its text baseline
      <DynamicIcon
        library={iconLibrary}
        name={icon}
        size={16}
        sx={{ display: 'block', lineHeight: 0 }}
      />
    ) : (
      <DynamicIcon
        library={iconLibrary}
        sx={{ fontSize: '1.4rem !important', margin: { xs: 0 } }}
        name={icon}
      />
    ))
  );
  return (
    <StyledButton
      ref={ref}
      {...props}
      variant="text"
      endIcon={buttonIcon}
      sx={[secondaryButtonRootSx(size), ...toSxArray(sx)]}
    >
      <Typography
        className="button-text"
        variant="overline"
        sx={secondaryButtonLabelSx(size, !!buttonIcon)}
      >
        {buttonText}
      </Typography>
    </StyledButton>
  );
});
SecondaryButton.displayName = 'SecondaryButton';
